import { useQueryClient } from '@tanstack/react-query';
import { addDays } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AddArchiveJobParameters } from '../../../../../../typings/api/skymap/rest/v1/project';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { formattedDate } from '../../../../utils/dateUtils';
import { publishNotification } from '../../../../utils/notifications/publish-notification';
import { RequestParamsToApiPaths } from '../../../../utils/typescript-utils';
import { useDialog } from '../../../hooks/use-dialog';
import { useErrorHandling } from '../../../hooks/use-error-handling';
import { AppContext, UserRole } from '../../../state/app-state';
import { ProjectContext } from '../../../state/project-state/project-state';
import { Button } from '../../button/button';
import { DateTimePickerDialog } from '../../date-time-picker-dialog/date-time-picker-dialog';
import { Dialog } from '../../dialog/dialog';
import { Icon } from '../../icon/icon';
import { InfoBox } from '../../info-box/info-box';
import { LabelledContainer } from '../../labelled-container/labelled-container';
import { Stack } from '../../stack/stack';
import { TextBox } from '../../text-box/text-box';
import { TextBoxWithRightButton } from '../styles/styles';

enum Status {
  Idle,
  Pending,
  Error,
  Done,
}

interface Props {
  onClose: (archived: boolean) => void;
}

const InactivateProjectDialog = (props: Props) => {
  const { t } = useTranslation();
  const earliestArchiveDate = addDays(new Date(), 7);
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const { currentUser } = React.useContext(AppContext);
  const { project } = React.useContext(ProjectContext);
  const [status, setStatus] = React.useState(Status.Idle);
  const { handleError, buildErrorList, includeErrorPaths } =
    useErrorHandling<RequestParamsToApiPaths<AddArchiveJobParameters>>();
  const [archiveDate, setArchiveDate] = React.useState<Date | undefined>(earliestArchiveDate);
  const dateTimePickerDialog = useDialog();

  const archive = async () => {
    try {
      setStatus(Status.Pending);

      const projectService = SkyMapAxiosServiceFactory.instance.createProjectServiceV1();

      if (currentUser!.selectedRole === UserRole.CompanyAdmin) {
        await projectService.addArchiveJob({
          path: { projectId: project.id },
          body: {
            archiveDate: undefined,
          },
        });
      } else if (archiveDate) {
        await projectService.addArchiveJob({
          path: { projectId: project.id },
          body: {
            archiveDate: formattedDate(archiveDate),
          },
        });
      } else {
        await projectService.patchProject({
          path: { projectId: project.id },
          body: {
            status: 'DISABLED',
          },
        });
      }

      publishNotification('succ0309');
      queryClient.removeQueries({ queryKey: ['getProjects'] });

      if (currentUser!.selectedRole === UserRole.OrganizationAdmin) {
        props.onClose(true);
      } else {
        setStatus(Status.Done);
      }
    } catch (err: any) {
      setStatus(Status.Error);
      handleError(err);
    }
  };

  const cancel = () => {
    props.onClose(false);
  };

  const navigateToProjectListOverview = () => {
    void navigate('/projects', { replace: true });
  };

  return (
    <>
      <Dialog
        closeIcon={status === Status.Idle}
        closeOnDimmerClick={status === Status.Idle}
        useGoogleTranslate={true}
        width={500}
        onClose={cancel}
      >
        {{
          header: t('projectSettings.status.inactivateDialog.header', { ns: 'components' }),
          content: (
            <Stack spacing={1}>
              {status === Status.Done ? (
                <>
                  <InfoBox color="yellow" leftIcon={{ icon: ['fad', 'check'] }}>
                    {t('projectSettings.status.inactivateDialogDoneMessage', { ns: 'components' })}
                  </InfoBox>
                </>
              ) : (
                <>
                  <span>
                    {t('projectSettings.status.inactivateDialog.confirmQuestion', {
                      ns: 'components',
                    })}
                  </span>

                  {currentUser!.selectedRole === UserRole.OrganizationAdmin ? (
                    <InfoBox
                      color="yellow"
                      leftIcon={{ icon: ['fad', 'info'] }}
                      title={t('projectSettings.status.inactivateDialog.archiving', {
                        ns: 'components',
                      })}
                    >
                      {t('projectSettings.status.inactivateDialog.archivingInfo', {
                        ns: 'components',
                      })}
                    </InfoBox>
                  ) : (
                    <InfoBox color="yellow" leftIcon={{ icon: ['fad', 'info'] }}>
                      {t('projectSettings.status.inactivateDialog.restoreInfo', {
                        ns: 'components',
                      })}
                    </InfoBox>
                  )}

                  {buildErrorList({
                    filter: includeErrorPaths(['path.projectId']),
                    infoBoxProps: {
                      topMargin: false,
                    },
                  })}

                  {currentUser!.selectedRole === UserRole.OrganizationAdmin && (
                    <LabelledContainer
                      text={t('projectSettings.status.archiveDate', { ns: 'components' })}
                    >
                      <TextBoxWithRightButton>
                        <TextBox
                          readOnly={true}
                          readOnlyCursor="pointer"
                          value={
                            archiveDate
                              ? formattedDate(archiveDate)
                              : t('projectSettings.status.dateNotChosen', { ns: 'components' })
                          }
                          onClick={(event) => {
                            event.stopPropagation();
                            dateTimePickerDialog.show();
                          }}
                        />
                        <Button
                          aria-label="date-picker-button"
                          disabled={!archiveDate}
                          title={t('projectSettings.status.removeDate', { ns: 'components' })}
                          variant="contained"
                          onClick={() => {
                            setArchiveDate(undefined);
                          }}
                        >
                          <Icon icon={['fas', 'xmark']} />
                        </Button>
                      </TextBoxWithRightButton>
                    </LabelledContainer>
                  )}
                </>
              )}
            </Stack>
          ),
          footer: {
            right: (
              <>
                {status === Status.Done ? (
                  <Button variant="text" onClick={navigateToProjectListOverview}>
                    {t('close', { ns: 'common' })}
                  </Button>
                ) : (
                  <>
                    <Button
                      color="error"
                      disabled={status === Status.Pending}
                      loading={status === Status.Pending}
                      variant="contained"
                      onClick={archive}
                    >
                      {t('projectSettings.status.inactivateButton', { ns: 'components' })}
                    </Button>
                    <Button disabled={status === Status.Pending} variant="text" onClick={cancel}>
                      {t('close', { ns: 'common' })}
                    </Button>
                  </>
                )}
              </>
            ),
          },
        }}
      </Dialog>

      {dateTimePickerDialog.render(
        <DateTimePickerDialog
          displayTime={false}
          minDate={earliestArchiveDate}
          startDate={archiveDate ?? earliestArchiveDate}
          onCancel={() => {
            dateTimePickerDialog.hide();
          }}
          onDateChoosen={(date) => {
            setArchiveDate(date);
            dateTimePickerDialog.hide();
          }}
        />,
      )}
    </>
  );
};

export { Props as InactivateArchiveProjectDialogProps, InactivateProjectDialog };
